// Admin users — manage the backoffice staff (role='admin' in app.users).
// List view + new/edit/reset-password modals + per-row suspend/activate.
//
// Mirrors the Customers/Cars pattern: server-side filter+search via
// /admin/users; mutations invalidate the list query on success.

import { useEffect, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import {
  Avatar,
  PageHeader,
  ToolBtn,
  ToolChip,
  ToolSearch,
  Toolbar,
} from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { env } from '@/lib/env';
import {
  activateAdminUser,
  createAdminUser,
  fetchAdminUsers,
  resetAdminUserPassword,
  suspendAdminUser,
  updateAdminUser,
  type AdminUserRow,
  type AdminUsersListParams,
  type UpdateAdminUserInput,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

function resolveAvatarUrl(rel: string | null | undefined): string | null {
  if (!rel) return null;
  if (/^https?:\/\//.test(rel)) return rel;
  return `${env().PAYLOAD_URL}${rel}`;
}

type FilterKey = NonNullable<AdminUsersListParams['status']>;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'suspended', label: 'Suspended' },
];

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

export function AdminUsers() {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUserRow | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminUserRow | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id ?? null;

  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] });
  };

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, q, setPage]);

  const listQuery = useQuery({
    queryKey: ['admin', 'users', { filter, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminUsers(
        { status: filter, q: q.trim() || undefined, page, limit },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const suspendMut = useMutation({
    mutationFn: suspendAdminUser,
    onMutate: (id) => setBusyId(id),
    onSettled: () => setBusyId(null),
    onSuccess: invalidate,
  });
  const activateMut = useMutation({
    mutationFn: activateAdminUser,
    onMutate: (id) => setBusyId(id),
    onSettled: () => setBusyId(null),
    onSuccess: invalidate,
  });

  const rows = listQuery.data?.rows ?? [];
  const total = listQuery.data?.total ?? 0;

  const columns = useMemo<DataTableColumn<AdminUserRow>[]>(
    () => [
      {
        key: 'user',
        h: 'Admin',
        cell: (r) => (
          <div className="flex items-center gap-2">
            <Avatar name={r.displayName} size={26} src={resolveAvatarUrl(r.avatarUrl)} />
            <div className="min-w-0">
              <div className="truncate font-medium">
                {r.displayName}
                {Number(currentUserId) === r.id ? (
                  <span className="ml-1.5 rounded bg-[color:var(--bo-line)] px-1 py-0.5 text-[10px] uppercase tracking-wide text-[color:var(--bo-mute)]">
                    you
                  </span>
                ) : null}
              </div>
              <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">{r.email}</div>
            </div>
          </div>
        ),
      },
      { key: 'phone', h: 'Phone', cell: (r) => r.phone ?? '—' },
      {
        key: 'role',
        h: 'Role',
        cell: (r) => (
          <div className="flex flex-col text-[12px]">
            <span className="uppercase tracking-wide text-[color:var(--bo-mute)]">{r.role}</span>
            {r.staffRole ? (
              <span className="text-[11px] text-[color:var(--bo-ink)]">
                {r.staffRole.replace(/_/g, ' ')}
              </span>
            ) : r.role === 'admin' ? (
              <span className="text-[11px] italic text-[#b45309]">unassigned</span>
            ) : null}
          </div>
        ),
      },
      {
        key: '2fa',
        h: '2FA',
        cell: (r) =>
          r.twoFactorEnabled ? (
            <span className="text-[12px] text-[#166534]">On</span>
          ) : (
            <span className="text-[12px] text-[color:var(--bo-mute)]">Off</span>
          ),
      },
      { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
      {
        key: 'created',
        h: 'Joined',
        right: true,
        cell: (r) => (
          <span className="tabular-nums text-[12px] text-[color:var(--bo-mute)]">
            {fmtDate(r.createdAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        h: 'Actions',
        cell: (r) => {
          const busy = busyId === r.id;
          const isSelf = Number(currentUserId) === r.id;
          return (
            <div className="flex flex-wrap items-center gap-1.5">
              <RowAction busy={busy} onClick={() => setEditTarget(r)}>
                Edit
              </RowAction>
              <RowAction busy={busy} onClick={() => setResetTarget(r)}>
                Reset password
              </RowAction>
              {r.status === 'suspended' ? (
                <RowAction kind="primary" busy={busy} onClick={() => activateMut.mutate(r.id)}>
                  Activate
                </RowAction>
              ) : isSelf ? null : (
                <RowAction kind="danger" busy={busy} onClick={() => suspendMut.mutate(r.id)}>
                  Suspend
                </RowAction>
              )}
            </div>
          );
        },
      },
    ],
    [activateMut, busyId, currentUserId, suspendMut],
  );

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="System"
        title="Backoffice users"
        sub={`${total.toLocaleString()} admin${total === 1 ? '' : 's'}${
          filter === 'all' ? '' : ` · ${filter}`
        }.`}
        actions={
          <ToolBtn icon="plus" onClick={() => setCreateOpen(true)}>
            New admin
          </ToolBtn>
        }
      />
      {listQuery.error ? (
        <div
          role="alert"
          className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]"
        >
          Couldn&apos;t load admins: {(listQuery.error as Error).message}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search email or name…" />
        {FILTERS.map((f) => (
          <ToolChip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </ToolChip>
        ))}
      </Toolbar>
      <DataTable
        columns={columns}
        rows={rows}
        emptyLabel={listQuery.isLoading ? 'Loading…' : 'No matching admins.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />

      {createOpen ? (
        <NewAdminModal onClose={() => setCreateOpen(false)} onCreated={() => {
          setCreateOpen(false);
          invalidate();
        }} />
      ) : null}
      {editTarget ? (
        <EditAdminModal
          row={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            invalidate();
          }}
        />
      ) : null}
      {resetTarget ? (
        <ResetPasswordModal
          row={resetTarget}
          onClose={() => setResetTarget(null)}
          onSaved={() => {
            setResetTarget(null);
            invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

// ─── Modals ──────────────────────────────────────────────────────────

const FIELD =
  'h-9 w-full rounded-lg border border-[color:var(--bo-line)] bg-white px-3 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-mute)]';
const LBL = 'text-[12px] font-medium text-[color:var(--bo-ink)]';

function ModalShell({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg space-y-3 rounded-xl border border-[color:var(--bo-line)] bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-[15px] font-semibold text-[color:var(--bo-ink)]">{title}</h2>
          {description ? (
            <p className="mt-0.5 text-[12px] text-[color:var(--bo-mute)]">{description}</p>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

function readApiErrorCode(err: unknown): string | null {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    return String((err.body as { error?: unknown }).error);
  }
  return null;
}

function NewAdminModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'active' | 'pending'>('active');

  const mut = useMutation({
    mutationFn: () =>
      createAdminUser({
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        phone: phone.trim() || undefined,
        password,
        status,
      }),
    onSuccess: onCreated,
  });

  const errorMessage = (() => {
    const code = readApiErrorCode(mut.error);
    if (code === 'email_taken') return 'An account with that email already exists.';
    if (code === 'invalid_payload') return 'Please check the form values and try again.';
    if (mut.error) return 'Could not create the admin. Please try again.';
    return null;
  })();

  const canSubmit =
    /^\S+@\S+\.\S+$/.test(email.trim()) && password.length >= 8 && !mut.isPending;

  return (
    <ModalShell
      title="New backoffice admin"
      description="Creates an active admin account. Share the password out of band — they can change it after signing in."
      onClose={onClose}
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mut.mutate();
        }}
      >
        {errorMessage ? (
          <div className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
            {errorMessage}
          </div>
        ) : null}

        <label className="block space-y-1">
          <span className={LBL}>Email *</span>
          <input
            autoFocus
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@drivecars.ai"
            className={FIELD}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className={LBL}>First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={FIELD} />
          </label>
          <label className="block space-y-1">
            <span className={LBL}>Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={FIELD} />
          </label>
        </div>

        <label className="block space-y-1">
          <span className={LBL}>Phone</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className={FIELD} />
        </label>

        <label className="block space-y-1">
          <span className={LBL}>Password * (min 8 chars)</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={FIELD}
          />
        </label>

        <label className="block space-y-1">
          <span className={LBL}>Status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'active' | 'pending')}
            className={FIELD}
          >
            <option value="active">active</option>
            <option value="pending">pending</option>
          </select>
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <ToolBtn onClick={onClose}>Cancel</ToolBtn>
          <ToolBtn icon="check" onClick={() => canSubmit && mut.mutate()}>
            {mut.isPending ? 'Creating…' : 'Create admin'}
          </ToolBtn>
        </div>
      </form>
    </ModalShell>
  );
}

function EditAdminModal({
  row,
  onClose,
  onSaved,
}: {
  row: AdminUserRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(row.firstName ?? '');
  const [lastName, setLastName] = useState(row.lastName ?? '');
  const [phone, setPhone] = useState(row.phone ?? '');
  // Backoffice scoped role. Empty string in the form maps to NULL on the
  // server (revoke all section access while keeping role='admin').
  const [staffRole, setStaffRole] = useState<string>(row.staffRole ?? '');
  const [status, setStatus] = useState<UpdateAdminUserInput['status']>(row.status);

  const mut = useMutation({
    mutationFn: () =>
      updateAdminUser(row.id, {
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        phone: phone.trim() || null,
        staffRole: (staffRole || null) as UpdateAdminUserInput['staffRole'],
        status,
      }),
    onSuccess: onSaved,
  });

  const errorMessage = (() => {
    const code = readApiErrorCode(mut.error);
    if (code === 'cannot_demote_self') return 'You can’t demote your own account.';
    if (code === 'cannot_suspend_self') return 'You can’t suspend your own account.';
    if (mut.error) return 'Could not save changes. Please try again.';
    return null;
  })();

  return (
    <ModalShell
      title={`Edit ${row.displayName}`}
      description={row.email}
      onClose={onClose}
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!mut.isPending) mut.mutate();
        }}
      >
        {errorMessage ? (
          <div className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
            {errorMessage}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className={LBL}>First name</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={FIELD} />
          </label>
          <label className="block space-y-1">
            <span className={LBL}>Last name</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={FIELD} />
          </label>
        </div>

        <label className="block space-y-1">
          <span className={LBL}>Phone</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} className={FIELD} />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className={LBL}>Role</span>
            {/* Scoped role drives sidebar visibility + per-section API
                access. Leave empty to sandbox the user (keeps them as
                role='admin' on the API but every requireSection 403s).
                See docs/superpowers/specs/2026-05-29-backoffice-roles-design.md. */}
            <select
              value={staffRole}
              onChange={(e) => setStaffRole(e.target.value)}
              className={FIELD}
            >
              <option value="">— unassigned (no scoped access) —</option>
              <option value="super_admin">Super admin (everything)</option>
              <option value="operations">Operations</option>
              <option value="finance">Finance</option>
              <option value="marketing">Marketing</option>
              <option value="support">Support</option>
              <option value="viewer">Viewer (read-only)</option>
            </select>
          </label>
          <label className="block space-y-1">
            <span className={LBL}>Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as UpdateAdminUserInput['status'])}
              className={FIELD}
            >
              <option value="active">active</option>
              <option value="pending">pending</option>
              <option value="suspended">suspended</option>
            </select>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <ToolBtn onClick={onClose}>Cancel</ToolBtn>
          <ToolBtn icon="check" onClick={() => !mut.isPending && mut.mutate()}>
            {mut.isPending ? 'Saving…' : 'Save changes'}
          </ToolBtn>
        </div>
      </form>
    </ModalShell>
  );
}

function ResetPasswordModal({
  row,
  onClose,
  onSaved,
}: {
  row: AdminUserRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');

  const mut = useMutation({
    mutationFn: () => resetAdminUserPassword(row.id, pw),
    onSuccess: onSaved,
  });

  const errorMessage = (() => {
    if (mut.error) return 'Reset failed. Please try again.';
    return null;
  })();

  const mismatch = pw.length > 0 && confirm.length > 0 && pw !== confirm;
  const canSubmit = pw.length >= 8 && pw === confirm && !mut.isPending;

  return (
    <ModalShell
      title={`Reset password for ${row.displayName}`}
      description="Set a new password and deliver it out of band. Existing login attempts and lockout state are cleared."
      onClose={onClose}
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) mut.mutate();
        }}
      >
        {errorMessage ? (
          <div className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
            {errorMessage}
          </div>
        ) : null}

        <label className="block space-y-1">
          <span className={LBL}>New password (min 8 chars)</span>
          <input
            autoFocus
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className={FIELD}
          />
        </label>
        <label className="block space-y-1">
          <span className={LBL}>Confirm</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={FIELD}
          />
        </label>
        {mismatch ? (
          <p className="text-[12px] text-[#9f1239]">Passwords don&apos;t match.</p>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-1">
          <ToolBtn onClick={onClose}>Cancel</ToolBtn>
          <ToolBtn icon="check" onClick={() => canSubmit && mut.mutate()}>
            {mut.isPending ? 'Resetting…' : 'Reset password'}
          </ToolBtn>
        </div>
      </form>
    </ModalShell>
  );
}
